import { parseCurrencyInput } from '@/features/quotes/utils/group-life/format';
import type { GroupLifeTarifacaoLife } from '@/services/group-life';

type LifeWithCapital = {
  policyElementId?: number | string;
  cpf?: string;
  capital: string;
};

function onlyDigits(value?: string): string {
  return (value ?? '').replace(/\D/g, '');
}

/**
 * Resolve o capital EXIBIDO de cada vida na análise PME.
 *
 * O capital vem da tarifação (`/pricing`), que lê a vida no InsureMO — o
 * MESMO valor que o motor tarifa. O `capital` de `members` sai do store do
 * Portal, gravado na importação, e pode estar defasado (homologação 09-10:
 * R$ 500.000 na tela, R$ 800.000 no cálculo); em Múltiplo Salarial ele é
 * ainda o SALÁRIO. Cruza pelo `policyElementId` e, na falta dele, pelo CPF
 * completo. Sem correspondência mantém o valor de `members`.
 */
export function createLifeCapitalResolver(
  pricingLives: GroupLifeTarifacaoLife[]
) {
  const capitalByElementId = new Map<string, string>();
  const capitalByCpf = new Map<string, string>();
  for (const pricingLife of pricingLives) {
    if (!pricingLife.capital) continue;
    if (pricingLife.policyElementId) {
      capitalByElementId.set(
        String(pricingLife.policyElementId),
        pricingLife.capital
      );
    }
    const cpf = onlyDigits(pricingLife.cpf);
    // CPF mascarado não tem 11 dígitos e não serve para cruzar.
    if (cpf.length === 11) capitalByCpf.set(cpf, pricingLife.capital);
  }

  return (life: LifeWithCapital) =>
    (life.policyElementId != null
      ? capitalByElementId.get(String(life.policyElementId))
      : undefined) ??
    capitalByCpf.get(onlyDigits(life.cpf)) ??
    life.capital;
}

/**
 * O valor resolvido ainda é o salário gravado na vida. Em Múltiplo Salarial
 * a coluna multiplica; a tarifação, quando casa, já devolve o capital efetivo
 * e o número deixa de ser igual ao salário.
 */
export function isStoredSalaryCapital(
  resolved: string | undefined,
  stored: string | undefined
): boolean {
  const resolvedValue = parseCurrencyInput(resolved ?? '');
  const storedValue = parseCurrencyInput(stored ?? '');
  if (resolvedValue == null || storedValue == null) {
    return (resolved ?? '') === (stored ?? '');
  }
  return resolvedValue === storedValue;
}
